import { isValid, parse } from 'date-fns';

/**
 * Convert Brazilian date format to ISO format
 * @param dateStr - Date in DD/MM/YYYY format
 * @returns Date in YYYY-MM-DD format, or empty string if invalid
 * @example brazilianToISO("10/12/2025") => "2025-12-10"
 */
export function brazilianToISO(dateStr: string): string {
  if (!dateStr || dateStr.length !== 10) return '';
  const [day, month, year] = dateStr.split('/');
  if (!day || !month || !year) return '';
  return `${year}-${month}-${day}`;
}

/**
 * Convert ISO date format to Brazilian format
 * @param dateStr - Date in YYYY-MM-DD format
 * @returns Date in DD/MM/YYYY format, or empty string if invalid
 * @example isoToBrazilian("2025-12-10") => "10/12/2025"
 */
export function isoToBrazilian(dateStr: string): string {
  if (!dateStr || dateStr.length !== 10) return '';
  const [year, month, day] = dateStr.split('-');
  if (!day || !month || !year) return '';
  return `${day}/${month}/${year}`;
}

/**
 * Validate Brazilian date format and check if it's a valid date
 * @param dateStr - Date string to validate
 * @returns true if the date is valid and in DD/MM/YYYY format
 */
export function isValidBrazilianDate(dateStr: string): boolean {
  if (!dateStr || dateStr.length !== 10) return false;
  const regex = /^\d{2}\/\d{2}\/\d{4}$/;
  if (!regex.test(dateStr)) return false;

  const parsed = parse(dateStr, 'dd/MM/yyyy', new Date());
  return isValid(parsed);
}

/**
 * Apply mask to input value for DD/MM/YYYY format
 * @param value - Raw input value
 * @returns Masked value in DD/MM/YYYY format (partial or complete)
 * @example applyDateMask("10122025") => "10/12/2025"
 */
export function applyDateMask(value: string): string {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  // Apply mask progressively
  if (digits.length <= 2) {
    return digits;
  } else if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  } else {
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
  }
}
